/**
 * 司机活动运营后台类型定义，逐字对应 admin-plan.md §十二 的 GraphQL 契约。
 * Long 在 JSON 里是数字；金额按仓库惯例用 String；日期 yyyy-MM-dd、报名截止 yyyy-MM-dd HH:mm（均美西）；时间戳毫秒。
 */

export enum DriverActivityAdminStatus {
    DRAFT = 'DRAFT',
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    ENDED = 'ENDED',
    OFFLINE = 'OFFLINE',
}

/** 列表状态筛选的展示顺序 */
export const DRIVER_ACTIVITY_STATUS_OPTIONS: DriverActivityAdminStatus[] = [
    DriverActivityAdminStatus.DRAFT,
    DriverActivityAdminStatus.NOT_STARTED,
    DriverActivityAdminStatus.IN_PROGRESS,
    DriverActivityAdminStatus.ENDED,
    DriverActivityAdminStatus.OFFLINE,
];

export enum DriverActivityOperation {
    CREATE = 'CREATE',
    UPDATE = 'UPDATE',
    ONLINE = 'ONLINE',
    OFFLINE = 'OFFLINE',
    DELETE = 'DELETE',
    SYSTEM_START = 'SYSTEM_START',
    SYSTEM_END = 'SYSTEM_END',
}

/** 与 api-server DriverActivityTypeType 一致；本期后台只建出勤奖 */
export enum DriverActivityType {
    ATTENDANCE = 'ATTENDANCE',
    REFERRAL = 'REFERRAL',
    ORDER_SPRINT = 'ORDER_SPRINT',
}

export interface DriverActivityAdminLog {
    id: number;
    operation: DriverActivityOperation;
    operatorId?: number | null;
    /** 空 = 系统动作，前端按 i18n 显示「系统」 */
    operatorName?: string | null;
    /** 补充说明（如下线原因），原文展示不翻译 */
    description?: string | null;
    createdAt: number;
}

export interface DriverActivityAdminListItem {
    id: number;
    name: string;
    type: DriverActivityType;
    status: DriverActivityAdminStatus;
    startDate?: string | null;
    endDate?: string | null;
    latestLog?: DriverActivityAdminLog | null;
}

export interface DriverActivityAdminPage {
    items: DriverActivityAdminListItem[];
    page: number;
    size: number;
    total: number;
}

export interface DriverActivityRuleLevel {
    level: number;
    dailyDeliverQty: number;
    targetAttendDays: number;
    rewardAmount: string;
}

export interface DriverActivityRule {
    scopeId?: number | null;
    hubIds: number[];
    warehouseIds: number[];
    levels: DriverActivityRuleLevel[];
}

export interface DriverActivityRuleSectionText {
    key: string;
    content: string;
}

export interface DriverActivityRuleText {
    language: string;
    sections: DriverActivityRuleSectionText[];
}

export interface DriverActivityAdminDetail {
    id: number;
    name: string;
    type: DriverActivityType;
    status: DriverActivityAdminStatus;
    startDate?: string | null;
    endDate?: string | null;
    weekCount: number;
    signUpDeadline?: string | null;
    bannerS3Key?: string | null;
    bannerUrl?: string | null;
    needSignUp: boolean;
    needRealName: boolean;
    maxRewardAmount?: string | null;
    rules: DriverActivityRule[];
    ruleText: DriverActivityRuleText[];
    offlineReason?: string | null;
    /** 下线时间，epoch 毫秒 */
    offlineTime?: number | null;
    logs: DriverActivityAdminLog[];
    createdAt?: number | null;
    updatedAt?: number | null;
}

export interface DriverActivityRuleTemplateSection {
    key: string;
    order: number;
    required: boolean;
    defaultVariables: string[];
}

export interface DriverActivityRuleTemplate {
    sections: DriverActivityRuleTemplateSection[];
    variables: string[];
    languages: string[];
    sectionMaxLength: number;
    /** 后台配置：是否允许活动进行中报名。false 报名截止须 ≤ 开始日；true 放宽到 ≤ 结束日 */
    allowSignUpDuringActivity: boolean;
    /** 三语六段默认文案，新建活动时填入；编辑已有活动用详情的 ruleText */
    defaultTexts: DriverActivityRuleText[];
}

/** 预览正文分段：variable 非空表示这段 text 是该变量替换出来的值 */
export interface DriverActivityRenderedSegment {
    text: string;
    variable?: string | null;
}

/** 预览：后端已替换变量、标题已按语种；segments 为空时退回用 content 整段显示 */
export interface DriverActivityRenderedSection {
    key: string;
    title: string;
    content: string;
    segments?: DriverActivityRenderedSegment[] | null;
}

export interface DriverActivityRenderedRuleText {
    language: string;
    sections: DriverActivityRenderedSection[];
}

export interface DriverActivityAdminFiltersInput {
    name?: string | null;
    status?: DriverActivityAdminStatus | null;
    startDate?: string | null;
    endDate?: string | null;
}

export interface DriverActivityRuleLevelInput {
    level: number;
    /** 保存/上线必填；预览时没填传 null */
    dailyDeliverQty: number | null;
    targetAttendDays: number | null;
    rewardAmount: string | null;
}

export interface DriverActivityRuleInput {
    scopeId?: number | null;
    hubIds: number[];
    warehouseIds: number[];
    levels: DriverActivityRuleLevelInput[];
}

export interface DriverActivityRuleSectionTextInput {
    key: string;
    content: string;
}

export interface DriverActivityRuleTextInput {
    language: string;
    sections: DriverActivityRuleSectionTextInput[];
}

export interface SaveDriverActivityInput {
    id?: number | null;
    name: string;
    startDate: string;
    endDate: string;
    signUpDeadline?: string | null;
    bannerS3Key: string;
    needSignUp: boolean;
    needRealName: boolean;
    maxRewardAmount: string;
    rules: DriverActivityRuleInput[];
    ruleText: DriverActivityRuleTextInput[];
    ignoreTranslationWarning?: boolean | null;
}

export interface UpdateDriverActivityLimitedInput {
    id: number;
    bannerS3Key?: string | null;
    ruleText?: DriverActivityRuleTextInput[] | null;
    rules?: DriverActivityRuleInput[] | null;
    ignoreTranslationWarning?: boolean | null;
}

/** Hub / WHS 下拉数据源：后端已按运营配置剔除测试站点黑名单 */
export interface DriverActivitySiteWarehouseOption {
    id: number;
    name: string;
}

export interface DriverActivitySiteOption {
    hubId: number;
    hubName: string;
    warehouses: DriverActivitySiteWarehouseOption[];
}

/** 预览入参：表单当前值，未填的传空；ruleIndex 从 0 起（后端越界按第 0 条） */
export interface PreviewDriverActivityRuleTextInput {
    startDate?: string | null;
    endDate?: string | null;
    maxRewardAmount?: string | null;
    rules: DriverActivityRuleInput[];
    ruleIndex: number;
    ruleText: DriverActivityRuleTextInput[];
    language: string;
}

/** 后端「中文已改、英/西未同步」的 messageKey，前端捕到后弹确认再带 ignoreTranslationWarning 重提 */
export const TRANSLATION_STALE_MESSAGE_KEY = 'errors.business.driverActivity.translationStale';
